// Net-new Team + TeamMember client — no existing frontend to adapt (the only prior artifact,
// flow-studio-api's agentTeamApiClient.ts, calls a nonexistent route and is dead code; see
// octopus-admin-spec/overview.md §4). Built directly against the real, already-complete,
// already-tested backend: BaseTeamController ([Route("api/v1/ai-agent/teams")]) and
// BaseTeamMemberController ([Route("api/v1/ai-agent/team-members")]), both confirmed by reading
// the controllers directly.
//
// V1 scope note (octopus-admin-spec's Open Question 2, resolved here): TeamMember's per-member
// routing-tuning columns (RoutingPriority/RoutingWeight/RoutingCondition/IsRoutingEnabled/
// MaxConcurrentRequests/MaxQueueSize/RoutingMetadata/LastRoutedTime/RoutingSuccessRate/
// RoutingFailureCount) exist on the AIAgent_TeamMembers SQL table but are confirmed NOT mapped by
// the EF Core TeamMember entity (BizFirst.Ai.AiAgent.Domain.Entities.TeamMember.cs only exposes
// TeamMemberID/TeamID/AgentID/Role/Capabilities/JoinedOn/LeftOn/IsActive) — so full routing-config
// UI isn't just deferred by choice, the current API surface has nothing to bind it to. This
// client (and TeamMemberManager) therefore covers Create/Update/Delete/activate/deactivate/
// add-member/remove-member/update-role/assign-leader/remove-leader only, matching what the real
// entity actually exposes.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { LookupOption, TeamDTO, TeamFormValues, TeamMemberDTO } from '../types';

export class TeamAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  // --- Team Type lookup (BaseTeamTypeController, BizFirst.Ai.AiCommon.Api.Base — confirmed real
  // route by reading the controller directly: [Route("api/v1/ai-common/team-types")]) ---

  private teamTypeCache: LookupOption[] | null = null;

  async getTeamTypes(): Promise<AdminApiResult<LookupOption[]>> {
    if (this.teamTypeCache) return { success: true, data: this.teamTypeCache };
    const result = await this.postQuery<Array<Record<string, unknown>>>('/api/v1/ai-common/team-types/active', {
      PageInfo: { PageNumber: 1, PageSize: 200 },
    });
    if (!result.success || !Array.isArray(result.data)) return { ...result, data: [] };
    const options = result.data.map((item) => ({
      id: (item.ID as number) ?? (item.TeamTypeID as number) ?? 0,
      name: (item.Name as string) ?? String(item.TeamTypeID ?? ''),
      ...item,
    }));
    this.teamTypeCache = options;
    return { success: true, data: options };
  }

  // --- Team ---

  async getActiveTeams(): Promise<AdminApiResult<TeamDTO[]>> {
    const result = await this.postQuery<TeamDTO[]>('/api/v1/ai-agent/teams/active', {
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getTeamById(teamId: number): Promise<AdminApiResult<TeamDTO>> {
    return this.postQuery<TeamDTO>('/api/v1/ai-agent/teams/by-id', { ID: { ID: teamId } });
  }

  // Note: BaseTeamController's Create/Update actions bind the generic InsertWebRequest/
  // UpdateWebRequest (BizFirstFi.Go.Essentials.Domain.Requests — confirmed by reading both
  // classes directly), whose `Data` property is a plain `object?` holding the entity's fields
  // flat, NOT wrapped under a `{ Team: {...} }` envelope (that shape is specific to
  // ValidateTeamRequest's dedicated `Team` property, a different, team-only request type). This
  // matches exactly how AgentAdminApiClient.create/update and CredentialAdminApiClient.create/
  // update already send their entity fields — flat, nested under `data` only by
  // BaseAdminApiClient.post/put's nestData=true wrapping.
  async createTeam(team: TeamFormValues): Promise<AdminApiResult<TeamDTO>> {
    const response = await this.post<{ id?: { id?: number } }>('/api/v1/ai-agent/teams', team);
    const newID = response.data?.id?.id;
    if (response.success && newID) {
      return { ...response, data: { TeamID: newID, ...team } as TeamDTO };
    }
    return response as unknown as AdminApiResult<TeamDTO>;
  }

  async updateTeam(teamId: number, team: Partial<TeamFormValues>): Promise<AdminApiResult<TeamDTO>> {
    const response = await this.put<{ id?: { id?: number } }>(`/api/v1/ai-agent/teams/${teamId}`, {
      TeamID: teamId,
      ...team,
    });
    if (response.success) {
      return { ...response, data: { TeamID: teamId, ...team } as TeamDTO };
    }
    return response as unknown as AdminApiResult<TeamDTO>;
  }

  async deleteTeam(teamId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/teams/${teamId}`, { id: teamId });
  }

  // Note: activate/deactivate/assign-leader/remove-leader/name-exists bind team-specific
  // GoWebRequest subclasses (TeamActionRequests.cs — ActivateTeamRequest/DeactivateTeamRequest/
  // AssignLeaderRequest/RemoveLeaderRequest/TeamNameExistsRequest), each with its fields
  // (TeamID/LeaderAgentID/TeamName/ExcludeTeamID) directly on the request root — unlike Create/
  // Update, these are NOT `{ Data: {...} }`-wrapped. Sent via postQuery (root-level spread), not
  // post (which nests under `data`), matching agentApiClient.getById's identical root-level
  // `{ ID: {...} }` shape for the same kind of non-CRUD action/query request.
  async activateTeam(teamId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/teams/activate', { TeamID: { ID: teamId } });
  }

  async deactivateTeam(teamId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/teams/deactivate', { TeamID: { ID: teamId } });
  }

  async assignLeader(teamId: number, leaderAgentId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/teams/assign-leader', {
      TeamID: { ID: teamId },
      LeaderAgentID: { ID: leaderAgentId },
    });
  }

  async removeLeader(teamId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/teams/remove-leader', { TeamID: { ID: teamId } });
  }

  async teamNameExists(teamName: string, excludeTeamId?: number): Promise<AdminApiResult<boolean>> {
    const result = await this.postQuery<{ exists?: boolean }>('/api/v1/ai-agent/teams/name-exists', {
      TeamName: teamName,
      ...(excludeTeamId ? { ExcludeTeamID: { ID: excludeTeamId } } : {}),
    });
    return { ...result, data: Boolean(result.data?.exists) };
  }

  // --- TeamMember ---
  // Same root-level (not Data-wrapped) shape as the Team operations above —
  // TeamMemberActionRequests.cs's AddMemberRequest/RemoveMemberRequest/UpdateRoleRequest/
  // ActivateMemberRequest/DeactivateMemberRequest all extend GoWebRequest directly.

  async getMembersByTeam(teamId: number): Promise<AdminApiResult<TeamMemberDTO[]>> {
    const result = await this.postQuery<TeamMemberDTO[]>('/api/v1/ai-agent/team-members/by-team', {
      TeamID: { ID: teamId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async addMember(teamId: number, agentId: number, role: string): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/team-members/add-member', {
      TeamID: { ID: teamId },
      AgentID: { ID: agentId },
      Role: role,
    });
  }

  async removeMember(teamMemberId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/team-members/remove-member', { TeamMemberID: { ID: teamMemberId } });
  }

  async updateMemberRole(teamMemberId: number, role: string): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/team-members/update-role', {
      TeamMemberID: { ID: teamMemberId },
      Role: role,
    });
  }

  async activateMember(teamMemberId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/team-members/activate-member', { TeamMemberID: { ID: teamMemberId } });
  }

  async deactivateMember(teamMemberId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/team-members/deactivate-member', { TeamMemberID: { ID: teamMemberId } });
  }
}
